import { MAX_SELECTABLE_GENRE_TAGS } from '@arpeggi/shared'
import { Box } from '@arpeggi/ui'
import { ComponentProps, FC } from 'react'
import { GENRES } from '../../../client/Genres'
import { styled } from '../../../lib/stitches'
import Dropdown from '../../core/Dropdown'
import { FieldErrorLabel } from '../../core/FieldErrorLabel'
import { ModalComponents } from '../../core/Modal'
import { gray12, gray5, gray8 } from '../../platform/color'
import Multiselect from '../../studio/mint/Multiselect'
import ColorPicker from './mint-modal/ColorPicker'
import { loopStemKeySelectOptions } from './upload-sound-modal/MintSoundUploadPage'

const StyledFieldErrorLabel = styled(FieldErrorLabel, {
  marginTop: '12px',
  marginBottom: 0
})

interface FieldControls<T> {
  value?: T
  handleChange: (value?: T) => void
  error?: string
}

interface Props {
  formControls: {
    title: FieldControls<string>
    genres: {
      value: string[]
      handleAdd: (value?: string) => void
      handleRemove: (value?: string) => void
      error?: string
    }
    key: FieldControls<number>
    bpm: FieldControls<number>
    color?: FieldControls<string>
    customWalletAddress?: FieldControls<string>
  }
  isDisabled?: boolean
}

export const MintDetailsForm: FC<Props & ComponentProps<typeof Box>> = ({
  formControls: { title, genres, key, bpm, color, customWalletAddress },
  isDisabled,
  css
}) => {
  return (
    <Box column css={{ gap: '16px', ...css }}>
      <Box>
        <ModalComponents.FieldLabel>Track title*</ModalComponents.FieldLabel>
        <ModalComponents.TextField
          placeholder="Your track title"
          value={title.value}
          onChange={(e) => title.handleChange(e.target.value)}
          css={{ width: '100%', marginBottom: 0 }}
          disabled={isDisabled}
        />
        {title.error && <StyledFieldErrorLabel>{title.error}</StyledFieldErrorLabel>}
      </Box>
      <Box>
        <ModalComponents.FieldLabel>
          Genre* <span style={{ color: gray8 }}>(up to {MAX_SELECTABLE_GENRE_TAGS})</span>
        </ModalComponents.FieldLabel>
        <Multiselect
          values={new Set(genres.value)}
          options={GENRES}
          onSelect={genres.handleAdd}
          onRemove={genres.handleRemove}
          isDisabled={isDisabled}
        />
        {genres.error && <StyledFieldErrorLabel>{genres.error}</StyledFieldErrorLabel>}
      </Box>
      <Box>
        <ModalComponents.FieldLabel>Key*</ModalComponents.FieldLabel>
        <Dropdown
          placeholder="Key"
          options={loopStemKeySelectOptions()}
          value={key.value}
          onSelect={(val) => key.handleChange(val.value)}
          labelCSS={{
            fontSize: '14px',
            color: gray12,
            width: '100%'
          }}
          style={{
            border: `1px solid ${gray5}`,
            width: '100%'
          }}
          disabled={isDisabled}
        />
        {key.error && <StyledFieldErrorLabel>{key.error}</StyledFieldErrorLabel>}
      </Box>
      <Box>
        <ModalComponents.FieldLabel>BPM*</ModalComponents.FieldLabel>
        <ModalComponents.TextField
          type="number"
          placeholder="Enter BPM"
          value={bpm.value?.toString()}
          onChange={(e) => bpm.handleChange(Number(e.target.value))}
          css={{ width: '100%', marginBottom: 0 }}
          min="60"
          max="320"
          disabled={isDisabled}
        />
        {bpm.error && <StyledFieldErrorLabel>{bpm.error}</StyledFieldErrorLabel>}
      </Box>
      {color && (
        <Box>
          <ModalComponents.FieldLabel>Select your token color*</ModalComponents.FieldLabel>
          <ColorPicker
            onPick={color.handleChange}
            currentColor={color.value}
            isDisabled={isDisabled}
          />
          {color.error && <StyledFieldErrorLabel>{color.error}</StyledFieldErrorLabel>}
        </Box>
      )}
      {customWalletAddress && (
        <Box>
          <ModalComponents.FieldLabel>Custom wallet address</ModalComponents.FieldLabel>
          <ModalComponents.TextField
            placeholder="Mint on behalf of someone else (admin only)"
            value={customWalletAddress.value}
            onChange={(e) => customWalletAddress.handleChange(e.target.value)}
            css={{ width: '100%', marginBottom: 0 }}
            disabled={isDisabled}
          />
        </Box>
      )}
    </Box>
  )
}
